import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/common/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, AlertCircle } from "lucide-react";
import { GoogleSignInButton } from "@/components/auth/GoogleSignInButton";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    const cleanEmail = email.trim().toLowerCase();
    if (!cleanEmail || !password) {
      setError("Inserisci email e password.");
      return;
    }
    setLoading(true);
    try {
      const u = await login(cleanEmail, password);
      toast.success("Accesso effettuato");
      navigate(u.role === "client" ? "/portale" : "/");
    } catch (err) {
      let msg;
      if (!err?.response) {
        // Nessuna risposta dal server: problema di connessione
        msg = "Connessione assente o server non raggiungibile. Controlla la rete e riprova.";
      } else if (err.response.status === 401) {
        msg = "Email o password non validi. Verifica i dati e riprova.";
      } else if (err.response.status === 422) {
        msg = "Email non valida. Controlla di aver inserito un indirizzo corretto.";
      } else if (err.response.status >= 500) {
        msg = "Servizio momentaneamente non disponibile. Riprova tra poco.";
      } else {
        msg = err.response.data?.detail || "Accesso non riuscito. Riprova.";
      }
      setError(msg);
      toast.error(msg);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Brand panel */}
      <div className="hidden lg:flex flex-col justify-between hi-plaque text-[hsl(36_44%_96%)] p-12 relative overflow-hidden">
        <div className="absolute inset-0 opacity-30" style={{ backgroundImage: "radial-gradient(800px 400px at 30% 20%, hsla(42 60% 52% / 0.25), transparent 60%)" }} />
        <div className="relative">
          <img src="/logo.jpg" alt="Hair Identity" className="w-56 rounded-xl" />
        </div>
        <div className="relative">
          <h2 className="font-heading text-4xl font-semibold leading-tight">Il gestionale su misura<br/>per il tuo salone.</h2>
          <p className="mt-4 text-sm opacity-70 max-w-md">Agenda, clienti, cassa, team e analytics in un'unica piattaforma elegante. Pensata per l'uso quotidiano in salone.</p>
        </div>
        <div className="relative text-xs uppercase tracking-[0.25em] opacity-60">Specialist of Hair Color</div>
      </div>

      {/* Form */}
      <div className="flex items-center justify-center p-6 hi-app-bg">
        <div className="w-full max-w-sm">
          <div className="lg:hidden mb-8 flex justify-center"><Logo showText size={56} /></div>
          <h1 className="font-heading text-3xl font-semibold">Bentornato</h1>
          <p className="text-sm text-muted-foreground mt-1 mb-6">Accedi al gestionale Identity OS</p>

          {error && (
            <div className="mb-4 flex items-start gap-2 rounded-lg border border-destructive/40 bg-destructive/10 px-3 py-2.5 text-sm text-destructive" role="alert" data-testid="login-error">
              <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" value={email}
                onChange={(e) => { setEmail(e.target.value); if (error) setError(""); }}
                required data-testid="login-email-input"
                autoComplete="email" inputMode="email" autoCapitalize="none" autoCorrect="off" spellCheck={false} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" value={password}
                onChange={(e) => { setPassword(e.target.value); if (error) setError(""); }}
                required data-testid="login-password-input" autoComplete="current-password" />
            </div>
            <Button type="submit" className="w-full" disabled={loading} data-testid="login-submit-button">
              {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Accedi
            </Button>
          </form>
          <div className="flex items-center gap-3 my-4">
            <span className="h-px flex-1 bg-border" />
            <span className="text-[11px] uppercase tracking-wider text-muted-foreground">oppure</span>
            <span className="h-px flex-1 bg-border" />
          </div>
          <GoogleSignInButton testid="login-google-btn" />
          <p className="text-sm text-center text-muted-foreground mt-4">Sei un cliente? <a href="/registrati" className="text-[hsl(var(--hi-gold))] font-medium" data-testid="login-register-link">Crea un account</a></p>
        </div>
      </div>
    </div>
  );
}
